/**
 * Vue I18n Configuration (Vue 3 + vue-i18n 11)
 * 
 * @version 2.0.0-alpha.3
 * @description Internationalization setup using vue-i18n 11
 * @note vue-i18n 11 removed legacy mode - Composition API only
 */

import { createI18n } from 'vue-i18n'
import axios from 'axios'

// Import locale messages (only existing files)
import it from './locales/it.json'
import en from './locales/en.json'
import fr from './locales/fr.json'

// =============================================================================
// Types
// =============================================================================

export type MessageSchema = typeof it
export type SupportedLocale = 'it' | 'en' | 'fr'

// =============================================================================
// Create I18n Instance (vue-i18n 11 - Composition API only)
// =============================================================================

const i18n = createI18n<MessageSchema, SupportedLocale, false>({
  legacy: false, // Required for TypeScript: makes (typeof options)['legacy'] resolve to literal `false`
  globalInjection: true,
  locale: import.meta.env.VITE_I18N_LOCALE || 'it',
  fallbackLocale: import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'it',
  messages: {
    it,
    en,
    fr,
  },
  missingWarn: false,
  fallbackWarn: false,
})

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Set the active locale
 */
export function setI18nLanguage(locale: SupportedLocale): SupportedLocale {
  // vue-i18n 11 uses .global.locale.value directly
  i18n.global.locale.value = locale
  
  // Update axios headers
  axios.defaults.headers.common['Accept-Language'] = locale
  
  // Update HTML lang attribute
  document.documentElement.setAttribute('lang', locale)
  
  return locale
}

/**
 * Get the current locale
 */
export function getCurrentLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale: string): locale is SupportedLocale {
  return ['it', 'en', 'fr'].includes(locale)
}

/**
 * Get browser preferred locale
 */
export function getBrowserLocale(): SupportedLocale {
  const browserLocale = navigator.language.split('-')[0]
  return isLocaleSupported(browserLocale) ? browserLocale : 'it'
}

// =============================================================================
// Export
// =============================================================================

export default i18n
